import { useState } from 'react';
import {
    obtenerTelefonosPorEmpleado,
    agregarTelefonoAEmpleado,
    actualizarTelefonoEmpleado,
    eliminarTelefonoDeEmpleado
} from '../../../services/empleado/empleadoDetalleService';

// Función auxiliar local (o importar de un utils.js si existe)
const extraerId = (dato) => {
    if (!dato) return null;
    if (typeof dato === 'object' && dato.configuracionDetId) return dato.configuracionDetId;
    if (!isNaN(parseInt(dato))) return parseInt(dato);
    return dato;
};

const emptyPhone = { id: null, tipo: null, numero: '', referencia: '', principal: false };

export function useTelefonos({ empleadoId, toast }) {

    // 1. ESTADOS
    const [phones, setPhones] = useState([]);
    const [phoneData, setPhoneData] = useState(emptyPhone);
    const [phoneDialog, setPhoneDialog] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [loading, setLoading] = useState(false);

    // 2. FUNCIÓN DE CARGA
    const recargarTelefonos = async (id) => {
        
        if (!id) { setPhones([]); return; }
        try {
            const nuevosTelefonos = await obtenerTelefonosPorEmpleado(id);
            // Mantenemos un orden consistente por ID
            const formateados = nuevosTelefonos.map(p => ({
                id: p.telefonoId,
                numero: p.numero,
                referencia: p.referencia,
                principal: p.esPrincipal,
                tipo: extraerId(p.parentezcoConfigDet) || p.parentezcoConfigDetId
            })).sort((a, b) => a.id - b.id); // Orden estable
            
            setPhones(formateados);
        } catch (e) {
            console.error("Error al recargar teléfonos:", e);
        }
    };

    // 3. ACCIONES MODALES
    const openNewPhone = () => {
        setPhoneData(emptyPhone);
        setSubmitted(false);
        setPhoneDialog(true);
    };

    const openEditPhone = (rowData) => {
        setPhoneData({ ...rowData });
        setPhoneDialog(true);
    };

    // 4. LÓGICA DE GUARDADO (CREATE / UPDATE)
    const savePhone = async () => {
        if (loading) return;

        if (!empleadoId) { 
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'ID de empleado perdido.' });
            return; }
        if (!phoneData.tipo || !phoneData.numero) { setSubmitted(true); return; }

        // Lógica de exclusividad local: si este es principal, los demás dejan de serlo
        const esNuevoPrincipal = phoneData.principal;

        const payload = {
            empleadoId: empleadoId,
            numero: phoneData.numero,
            referencia: phoneData.referencia || '',
            esPrincipal: esNuevoPrincipal,
            parentezcoConfigDetId: phoneData.tipo
        };

        try {
            setLoading(true);
            if (phoneData.id) {
                await actualizarTelefonoEmpleado({ ...payload, telefonoId: phoneData.id });
                toast.current.show({ severity: 'success', summary: 'Actualizado' });
            } else {
                await agregarTelefonoAEmpleado(payload);
                toast.current.show({ severity: 'success', summary: 'Creado' });
            }

            // REPARACIÓN: En lugar de solo recargar, limpiamos el estado local 
            // para asegurar la exclusividad antes de la sincronización.
            await recargarTelefonos(empleadoId);
            
            setPhoneDialog(false);
            setPhoneData(emptyPhone);
            setSubmitted(false);
        } catch (error) {
            console.error("Error guardando teléfono:", error);
            toast.current.show({ severity: 'error', summary: 'Error' });
        } finally {
            setLoading(false);
        }
    };

    // 5. LÓGICA DE ELIMINADO
    const deletePhone = async (rowData) => {
        if (!window.confirm(`¿Estás seguro de eliminar el teléfono ${rowData.numero}?`)) {
            return;
        }
        if (!empleadoId) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'ID de empleado perdido.' });
            return;
        }

        try {
            await eliminarTelefonoDeEmpleado(rowData.id);
            toast.current.show({ severity: 'success', summary: 'Eliminado', detail: 'Teléfono eliminado.' });
            await recargarTelefonos(empleadoId);
        } catch (error) {
            console.error("Error eliminando teléfono:", error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el teléfono.' });
        }
    };

    // 6. LÓGICA DE TELÉFONO PRINCIPAL (Exclusivo)
    const handleSetPrincipalPhone = async (phone) => {
        if (phone.principal) return; 

        // 1. Cambio Optimista Inmediato (Mejora UX)
        const originalPhones = [...phones];
        setPhones(prev => prev.map(p => ({
            ...p,
            principal: p.id === phone.id
        })));

        try {
            const payload = {
                empleadoId,
                telefonoId: phone.id,
                numero: phone.numero,
                esPrincipal: true,
                parentezcoConfigDetId: phone.tipoId,
            };

            await actualizarTelefonoEmpleado(payload);
            toast.current.show({ severity: 'success', summary: 'Actualizado', detail: 'Nuevo teléfono principal' });
        } catch (error) {
            setPhones(originalPhones); // Rollback si falla
            toast.current.show({ severity: 'error', summary: 'Error' });
        }
    };

    // RETORNO DE LA INTERFAZ DEL HOOK
    return {
        phones,
        phoneData,
        setPhoneData,
        setPhones,
        phoneDialog,
        setPhoneDialog,
        submitted,
        openNewPhone,
        openEditPhone,
        savePhone,
        deletePhone,
        handleSetPrincipalPhone,
        recargarTelefonos,
        loading
    };
}